import React from 'react';
import { theme } from '../styles/theme';
import Container from '../components/ui/Container';
import FooterPageContent from '../components/layout/FooterPageContent';

const PrivacyPolicyPage = () => {
  const pageStyles = {
    padding: `${theme.spacing.xxl} 0`,
    backgroundColor: theme.colors.background,
    minHeight: '100vh',
  };

  const contentStyles = {
    maxWidth: '900px',
    margin: '0 auto',
    padding: `0 ${theme.spacing.lg}`,
  };

  const headingStyles = {
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: theme.typography.fontSize.xxl,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.primary,
    marginBottom: theme.spacing.lg,
    textAlign: 'center',
  };

  const subHeadingStyles = {
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.primaryDark,
    marginTop: theme.spacing.xl,
    marginBottom: theme.spacing.md,
  };

  const sectionHeadingStyles = {
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.primary,
    marginTop: theme.spacing.lg,
    marginBottom: theme.spacing.sm,
  };

  const paragraphStyles = {
    fontSize: theme.typography.fontSize.base,
    lineHeight: theme.typography.lineHeight.relaxed,
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
  };

  const listStyles = {
    fontSize: theme.typography.fontSize.base,
    lineHeight: theme.typography.lineHeight.relaxed,
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
    paddingLeft: theme.spacing.xl,
  };

  const contactBoxStyles = {
    backgroundColor: theme.colors.neutral,
    padding: theme.spacing.lg,
    borderRadius: theme.borderRadius.md,
    marginTop: theme.spacing.xl,
    marginBottom: theme.spacing.lg,
  };

  const lastUpdatedStyles = {
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginBottom: theme.spacing.xl,
    fontStyle: 'italic',
  };

  return (
    <FooterPageContent slug="privacy">
    <div style={pageStyles}>
      <Container>
        <div style={contentStyles}>
          <h1 style={headingStyles}>BS Trading Co. – Privacy Policy</h1>
          <p style={lastUpdatedStyles}>Last Updated: January 2026</p>

          <p style={paragraphStyles}>
            BS Trading Co. ("we," "our," or "us") is committed to protecting the privacy and security of our customers, 
            website visitors, and users of our online services. This Privacy Policy explains how we collect, use, disclose, 
            and safeguard your information when you interact with us online, by phone, or in person.
          </p>

          <p style={paragraphStyles}>
            If you do not agree with this Privacy Policy, please discontinue use of our services.
          </p>

          <h2 style={subHeadingStyles}>1. Company Information</h2>
          <div style={contactBoxStyles}>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>BS Trading Co.</strong>
            </p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>253 Loy St</p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>Burleson, TX 76028</p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>Phone:</strong> 817-447-4433
            </p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>Toll-Free:</strong> 1-800-554-0910
            </p>
            <p style={{ ...paragraphStyles, marginBottom: 0 }}>
              <strong>Email:</strong> <a href="mailto:Orders@BSTrading.com" style={{ color: theme.colors.primary }}>Orders@BSTrading.com</a>
            </p>
          </div>

          <h2 style={subHeadingStyles}>2. Information We Collect</h2>
          <p style={paragraphStyles}>We may collect the following types of information:</p>

          <h3 style={sectionHeadingStyles}>A. Personal Information</h3>
          <ul style={listStyles}>
            <li>Name</li>
            <li>Email address</li>
            <li>Phone number</li>
            <li>Billing and shipping address</li>
            <li>Account login information (if applicable)</li>
          </ul>

          <h3 style={sectionHeadingStyles}>B. Order & Transaction Information</h3>
          <ul style={listStyles}>
            <li>Products purchased</li>
            <li>Payment information (processed securely through Stripe)</li>
            <li>Purchase history and preferences</li>
          </ul>

          <h3 style={sectionHeadingStyles}>C. Automatically Collected Information</h3>
          <p style={paragraphStyles}>When you visit our website, we may automatically collect:</p>
          <ul style={listStyles}>
            <li>IP address</li>
            <li>Browser type</li>
            <li>Device information</li>
            <li>Cookies and tracking data</li>
            <li>Pages visited and browsing behavior</li>
          </ul>

          <h3 style={sectionHeadingStyles}>D. Optional Information</h3>
          <p style={paragraphStyles}>
            If you contact customer support or create an account, we may collect:
          </p>
          <ul style={listStyles}>
            <li>Notes from communications</li>
            <li>Optional profile, business, or preference details</li>
          </ul>

          <h2 style={subHeadingStyles}>3. How We Use Your Information</h2>
          <p style={paragraphStyles}>We use the information we collect for:</p>
          <ul style={listStyles}>
            <li>Processing and fulfilling orders</li>
            <li>Customer service and communication</li>
            <li>Account creation and authentication</li>
            <li>Improving our website, products, and services</li>
            <li>Marketing, promotions, and email updates (you may opt out anytime)</li>
            <li>Fraud prevention and security</li>
            <li>Legal compliance and recordkeeping</li>
          </ul>

          <h2 style={subHeadingStyles}>4. How We Share Your Information</h2>
          <p style={paragraphStyles}>
            We do not sell your personal information. We may share your information with:
          </p>

          <h3 style={sectionHeadingStyles}>A. Service Providers</h3>
          <p style={paragraphStyles}>
            Trusted third-party partners that perform services on our behalf, such as:
          </p>
          <ul style={listStyles}>
            <li>Payment processors</li>
            <li>Shipping carriers</li>
            <li>Email and marketing platforms</li>
            <li>Website hosting and analytics services</li>
          </ul>
          <p style={paragraphStyles}>
            These providers only receive the information necessary to perform their tasks and are required to protect it.
          </p>

          <h3 style={sectionHeadingStyles}>B. Legal Requirements</h3>
          <p style={paragraphStyles}>We may disclose your information if required by:</p>
          <ul style={listStyles}>
            <li>Law enforcement</li>
            <li>Court orders</li>
            <li>Government regulations</li>
            <li>To protect our rights, safety, or property</li>
          </ul>

          <h2 style={subHeadingStyles}>5. Cookies & Tracking Technologies</h2>
          <p style={paragraphStyles}>We may use cookies, pixels, and analytics tools to:</p>
          <ul style={listStyles}>
            <li>Personalize your experience</li>
            <li>Improve website performance</li>
            <li>Understand user behavior</li>
            <li>Enable secure login and cart functionality</li>
          </ul>
          <p style={paragraphStyles}>You can manage cookie preferences through your browser settings.</p>

          <h2 style={subHeadingStyles}>6. Data Security</h2>
          <p style={paragraphStyles}>
            We implement reasonable administrative, technical, and physical safeguards to protect your information. 
            However, no online transmission or storage system is 100% secure, and we cannot guarantee absolute protection.
          </p>

          <h2 style={subHeadingStyles}>7. Your Rights & Choices</h2>
          <p style={paragraphStyles}>Depending on your location, you may have the right to:</p>
          <ul style={listStyles}>
            <li>Access the information we store about you</li>
            <li>Request corrections</li>
            <li>Request deletion of your data</li>
            <li>Opt out of marketing communications</li>
            <li>Disable cookies or similar tracking technologies</li>
          </ul>
          <p style={paragraphStyles}>
            To make a request, email us at: <a href="mailto:Orders@BSTrading.com" style={{ color: theme.colors.primary }}>Orders@BSTrading.com</a>
          </p>

          <h2 style={subHeadingStyles}>8. Children's Privacy</h2>
          <p style={paragraphStyles}>
            Our website and services are not intended for individuals under 13 years of age. We do not knowingly collect 
            information from children.
          </p>

          <h2 style={subHeadingStyles}>9. Third-Party Websites</h2>
          <p style={paragraphStyles}>
            Our website may contain links to external sites. We are not responsible for the content or privacy practices 
            of third-party websites.
          </p>

          <h2 style={subHeadingStyles}>10. Changes to This Privacy Policy</h2>
          <p style={paragraphStyles}>
            We may update this Privacy Policy periodically. Any changes will be posted on this page with an updated 
            "Last Updated" date. Continued use of our website or services indicates acceptance of any revisions.
          </p>

          <h2 style={subHeadingStyles}>11. Contact Us</h2>
          <p style={paragraphStyles}>
            If you have any questions regarding this Privacy Policy, please contact us:
          </p>
          <div style={contactBoxStyles}>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>BS Trading Co.</strong>
            </p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>253 Loy St</p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>Burleson, TX 76028</p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>Phone:</strong> 817-447-4433
            </p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>Toll-Free:</strong> 1-800-554-0910
            </p>
            <p style={{ ...paragraphStyles, marginBottom: 0 }}>
              <strong>Email:</strong> <a href="mailto:Orders@BSTrading.com" style={{ color: theme.colors.primary }}>Orders@BSTrading.com</a>
            </p>
          </div>
        </div>
      </Container>
    </div>
    </FooterPageContent>
  );
};

export default PrivacyPolicyPage;

